import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import DataApi from '../../api/DataApi';
import LookupLayout from './LookupLayout';
import { getEntitlementsSection } from '../../utils/entitlements';

const CORE_OBJECT_NAMES = ['account', 'contact', 'opportunity'];
const CORE_STANDARD_SET = new Set(CORE_OBJECT_NAMES);

const isEntitlementEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

/**
 * Lead convert modal: target objects must appear in lead_conversion_config.mappingObjects AND pass
 * company_subscription leadConversionMapping (allowlist/denylist for standard objects, allowCustomObject + denylist for custom).
 * Mirrors FieldManager / lead mapping entitlements.
 */
function filterMappingObjectsByLeadConversionEntitlements(mappingObjectNames, leadMappingConfig) {
  if (!leadMappingConfig || leadMappingConfig.allow === false) {
    return [];
  }
  const list = Array.isArray(mappingObjectNames) ? mappingObjectNames : [];
  const allowedSet = new Set(
    (leadMappingConfig.allowedObjects || []).map((n) => String(n).toLowerCase())
  );
  const mode = String(leadMappingConfig.mode || '').toLowerCase();
  const restrictAllowlist = mode === 'allowlist' && allowedSet.size > 0;
  const restrictDenylist = mode === 'denylist' && allowedSet.size > 0;
  const allowCustomObject = isEntitlementEnabled(leadMappingConfig.allowCustomObject);

  const subscriptionAllows = (name) => {
    const normalized = String(name || '').toLowerCase();
    if (!normalized) return false;
    const isCore = CORE_STANDARD_SET.has(normalized);
    if (isCore) {
      if (restrictAllowlist) return allowedSet.has(normalized);
      if (restrictDenylist) return !allowedSet.has(normalized);
      return true;
    }
    if (!allowCustomObject) return false;
    if (restrictDenylist && allowedSet.has(normalized)) return false;
    return true;
  };

  return list.filter(subscriptionAllows);
}

/** Core objects first (fixed order), then remaining names in mapping list order. */
function buildOrderedConversionTargets(rawMappingNames, entitlementFilteredNames) {
  const coreOrder = [...CORE_OBJECT_NAMES];
  const pass = new Set(entitlementFilteredNames.map((n) => String(n).toLowerCase()));
  const out = [];
  for (const core of coreOrder) {
    if (!pass.has(core)) continue;
    const original = rawMappingNames.find((m) => String(m).toLowerCase() === core);
    out.push(original != null ? original : core);
  }
  for (const name of rawMappingNames) {
    const low = String(name).toLowerCase();
    if (coreOrder.includes(low)) continue;
    if (pass.has(low)) out.push(name);
  }
  return out;
}

/**
 * Lead values for conversion: shallow record + header, then layout detail fields (details win on key clash).
 * Ensures first_name / last_name resolve whether they live on the row, header, or in sections (legacy firstname/lastname still read).
 */
function extractLeadFieldsFlat(leadData) {
  const leadFields = {};
  if (!leadData || typeof leadData !== 'object') {
    return leadFields;
  }
  const assignShallow = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    Object.keys(obj).forEach((key) => {
      if (key === 'details' || key === 'header') return;
      const v = obj[key];
      if (v !== undefined && v !== null) {
        leadFields[key] = v;
      }
    });
  };
  assignShallow(leadData);
  assignShallow(leadData.header);
  if (Array.isArray(leadData.details)) {
    leadData.details.forEach((section) => {
      if (section.fields && Array.isArray(section.fields)) {
        section.fields.forEach((field) => {
          if (field.fieldName != null) {
            leadFields[field.fieldName] = field.value;
          }
        });
      }
    });
  }
  return leadFields;
}

function pickLeadFirstName(leadFields) {
  const v = leadFields.first_name ?? leadFields.firstname;
  return v != null && String(v).trim() !== '' ? v : '';
}

function pickLeadLastName(leadFields) {
  const v = leadFields.last_name ?? leadFields.lastname;
  return v != null && String(v).trim() !== '' ? v : '';
}

/**
 * Contact and lead both use field names first_name / last_name on object API (DB columns match).
 * Not driven by field-mapping rows. Strip legacy firstname/lastname keys so INSERT does not target wrong columns.
 */
function applyStandardContactNameFromLead(mappedData, leadFields) {
  if (mappedData != null && typeof mappedData === 'object') {
    const empty = (x) => x === undefined || x === null || String(x).trim() === '';
    if (!empty(mappedData.firstname) && empty(mappedData.first_name)) {
      mappedData.first_name = mappedData.firstname;
    }
    if (!empty(mappedData.lastname) && empty(mappedData.last_name)) {
      mappedData.last_name = mappedData.lastname;
    }
    delete mappedData.firstname;
    delete mappedData.lastname;
  }

  const fn = pickLeadFirstName(leadFields);
  const ln = pickLeadLastName(leadFields);
  const empty = (x) => x === undefined || x === null || String(x).trim() === '';
  if (fn !== '' && empty(mappedData.first_name)) {
    mappedData.first_name = fn;
  }
  if (ln !== '' && empty(mappedData.last_name)) {
    mappedData.last_name = ln;
  }
}

/** "Create new contact" field: first word → first_name, remainder → last_name (overrides lead defaults). */
function applyContactNameFromConvertInput(mappedData, fullName) {
  if (mappedData == null || typeof mappedData !== 'object') return;
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return;
  const parts = trimmed.split(/\s+/);
  mappedData.first_name = parts[0] || '';
  mappedData.last_name = parts.slice(1).join(' ') || '';
  delete mappedData.firstname;
  delete mappedData.lastname;
}

//Comment
const LeadConvertModal = ({
  show,
  onHide,
  leadData,
  onConvert
}) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [existingAccount, setExistingAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Check existing account, 2: Show all selections
  const [accountOption, setAccountOption] = useState('existing'); // 'existing' or 'new'
  const [newAccountName, setNewAccountName] = useState('');

  // Contact states
  const [contactOption, setContactOption] = useState('new'); // 'existing' or 'new'
  const [selectedContact, setSelectedContact] = useState(null);
  const [existingContact, setExistingContact] = useState(null);
  const [newContactName, setNewContactName] = useState('');

  // Opportunity states
  const [opportunityOption, setOpportunityOption] = useState('new'); // 'existing' or 'new'
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [newOpportunityName, setNewOpportunityName] = useState('');
  /** null until conversion config + entitlement intersection is computed */
  const [configuredObjects, setConfiguredObjects] = useState(null);
  const [customSelections, setCustomSelections] = useState({});
  const [objectConfigMap, setObjectConfigMap] = useState({});
  const hasCheckedAccountRef = useRef(false);
  const hasFetchedConversionConfigRef = useRef(false);
  const hasSetDefaultNamesRef = useRef(false);

  const leadMappingConfig = getEntitlementsSection('leadConversionMapping', {});
  const migrationConfig = getEntitlementsSection('migration', {});
  const allowLeadMapping = leadMappingConfig?.allow !== false;
  const allowMigration = migrationConfig?.allow !== false;

  // Get company name from lead data (check multiple possible field names and locations)
  const getCompanyName = () => {
    // First check direct fields
    let companyName = leadData?.company;

    if (companyName) return companyName;

    // Check in header section
    if (leadData?.header) {
      companyName = leadData.header.company || leadData.header.company_name || leadData.header.name || leadData.header.name;
      if (companyName) return companyName;
    }

    // Check in details sections
    if (leadData?.details && Array.isArray(leadData.details)) {
      for (const section of leadData.details) {
        if (section.fields && Array.isArray(section.fields)) {
          for (const field of section.fields) {
            if (field.fieldName === 'company') {
              return field.value;
            }
          }
        }
      }
    }

    return null;
  };

  const buildDefaultChildMappings = useCallback((overrides = {}) => ({
    activity: '',
    task: '',
    attachment: '',
    note: '',
    email_log: '',
    quote: '',
    ...overrides
  }), []);

  const fetchLeadConversionConfig = useCallback(async (syncState = true) => {
    const applyConfiguredObjectsFromLeadConfig = (leadConfig) => {
      const rawList = Array.isArray(leadConfig.mappingObjects) ? leadConfig.mappingObjects : [];
      const coreOrder = [...CORE_OBJECT_NAMES];
      const logPrefix = '[LeadConvertModal]';
      if (!allowLeadMapping) {
        console.log(logPrefix, 'lead conversion (subscription allow=false)', {
          leadMappingConfig,
          apiLeadConversion: leadConfig,
          configuredObjects: [],
        });
        setConfiguredObjects([]);
        return;
      }
      if (rawList.length === 0) {
        const legacyPass = filterMappingObjectsByLeadConversionEntitlements(coreOrder, leadMappingConfig);
        const next = buildOrderedConversionTargets(coreOrder, legacyPass);
        console.log(logPrefix, 'conversion targets (legacy empty mappingObjects)', {
          leadMappingConfig,
          apiLeadConversion: leadConfig,
          rawMappingObjects: rawList,
          afterSubscriptionFilter: legacyPass,
          configuredObjects: next,
        });
        setConfiguredObjects(next);
        return;
      }
      const entitled = filterMappingObjectsByLeadConversionEntitlements(rawList, leadMappingConfig);
      const next = buildOrderedConversionTargets(rawList, entitled);
      console.log(logPrefix, 'conversion targets', {
        leadMappingConfig,
        apiLeadConversion: leadConfig,
        rawMappingObjects: rawList,
        afterSubscriptionFilter: entitled,
        configuredObjects: next,
      });
      setConfiguredObjects(next);
    };

    try {
      const configApi = new DataApi('objectconfig');
      const configResponse = await configApi.get('/lead/conversion-config');
      if (configResponse?.data?.success && configResponse.data.data) {
        const leadConfig = configResponse.data.data || {};
        if (syncState) {
          applyConfiguredObjectsFromLeadConfig(leadConfig);
        }
        return leadConfig;
      }
    } catch (configError) {
      console.warn('Failed to fetch lead conversion config:', configError);
    }
    if (syncState) {
      if (!allowLeadMapping) {
        console.log('[LeadConvertModal] conversion targets (fetch failed, allow=false)', {
          leadMappingConfig,
          configuredObjects: [],
        });
        setConfiguredObjects([]);
      } else {
        const legacyPass = filterMappingObjectsByLeadConversionEntitlements(
          [...CORE_OBJECT_NAMES],
          leadMappingConfig
        );
        const next = buildOrderedConversionTargets([...CORE_OBJECT_NAMES], legacyPass);
        console.log('[LeadConvertModal] conversion targets (fetch failed, subscription-filtered core fallback)', {
          leadMappingConfig,
          afterSubscriptionFilter: legacyPass,
          configuredObjects: next,
        });
        setConfiguredObjects(next);
      }
    }
    return {};
  }, [allowLeadMapping, leadMappingConfig]);

  useEffect(() => {
    if (!show) {
      hasFetchedConversionConfigRef.current = false;
      setConfiguredObjects(null);
      return;
    }
    if (hasFetchedConversionConfigRef.current) return;
    hasFetchedConversionConfigRef.current = true;
    fetchLeadConversionConfig(true);
  }, [show, fetchLeadConversionConfig]);

  useEffect(() => {
    if (!show || configuredObjects === null) return;
    console.log('[LeadConvertModal] configuredObjects (React state)', configuredObjects);
  }, [show, configuredObjects]);

  // Get lead name from lead data
  const getLeadName = () => {
    // First check direct fields
    let firstName = leadData?.firstname || leadData?.first_name;
    let lastName = leadData?.lastname || leadData?.last_name;

    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }

    // Check in header section
    if (leadData?.header) {
      firstName = leadData.header.firstname || leadData.header.first_name;
      lastName = leadData.header.lastname || leadData.header.last_name;
      if (firstName || lastName) {
        return `${firstName || ''} ${lastName || ''}`.trim();
      }
    }

    // Check in details sections
    if (leadData?.details && Array.isArray(leadData.details)) {
      let foundName = null;
      let foundFirstName = null;
      let foundLastName = null;

      for (const section of leadData.details) {
        if (section.fields && Array.isArray(section.fields)) {
          for (const field of section.fields) {
            // Check for name field first (can be hidden, but still contains full name)
            if (field.fieldName === 'name' && field.value) {
              foundName = field.value;
            }
            if (field.fieldName === 'first_name') {
              foundFirstName = field.value;
            }
            if (field.fieldName === 'last_name') {
              foundLastName = field.value;
            }
          }
        }
      }

      // Prefer name field if available (even if hidden)
      if (foundName) {
        return foundName.trim();
      }

      // Fall back to first_name and last_name combination
      if (foundFirstName || foundLastName) {
        return `${foundFirstName || ''} ${foundLastName || ''}`.trim();
      }
    }

    return 'N/A';
  };

  // Get lead email from lead data
  const getLeadEmail = () => {
    // First check direct fields
    let email = leadData?.email;

    if (email) return email;

    // Check in header section
    if (leadData?.header) {
      email = leadData.header.email;
      if (email) return email;
    }

    // Check in details sections
    if (leadData?.details && Array.isArray(leadData.details)) {
      for (const section of leadData.details) {
        if (section.fields && Array.isArray(section.fields)) {
          for (const field of section.fields) {
            if (field.fieldName === 'email') {
              return field.value;
            }
          }
        }
      }
    }

    return '';
  };

  const getLeadCurrency = () => {
    let currency = leadData?.currency || leadData?.currency_code;
    if (currency) return currency;

    if (leadData?.header) {
      currency = leadData.header.currency || leadData.header.currency_code;
      if (currency) return currency;
    }

    if (leadData?.details && Array.isArray(leadData.details)) {
      for (const section of leadData.details) {
        if (section.fields && Array.isArray(section.fields)) {
          for (const field of section.fields) {
            if (field.fieldName === 'currency' || field.fieldName === 'currency_code') {
              return field.value;
            }
          }
        }
      }
    }

    return '';
  };

  const customObjectNames = useMemo(() => {
    if (!configuredObjects) return [];
    return configuredObjects.filter(
      (objectName) => !CORE_OBJECT_NAMES.includes(String(objectName).toLowerCase())
    );
  }, [configuredObjects]);

  const isAccountEnabled =
    configuredObjects?.some((n) => String(n).toLowerCase() === 'account') ?? false;
  const isContactEnabled =
    configuredObjects?.some((n) => String(n).toLowerCase() === 'contact') ?? false;
  const isOpportunityEnabled =
    configuredObjects?.some((n) => String(n).toLowerCase() === 'opportunity') ?? false;

  // Check if account exists with company name when modal opens
  useEffect(() => {
    if (!show) {
      hasCheckedAccountRef.current = false;
      hasSetDefaultNamesRef.current = false;
      return;
    }

    if (configuredObjects === null) {
      return;
    }

    if (show) {
      const companyName = getCompanyName();
      const leadName = getLeadName();
      const leadEmail = getLeadEmail();

      const normalizedLeadName = leadName || '';
      const normalizedCompanyName = companyName || '';

      // Set default name values only once when modal opens so we don't overwrite user input
      if (!hasSetDefaultNamesRef.current) {
        hasSetDefaultNamesRef.current = true;
        setNewAccountName(normalizedCompanyName);
        setNewContactName(normalizedLeadName);
        setNewOpportunityName(
          normalizedLeadName && normalizedCompanyName
            ? `${normalizedLeadName} - ${normalizedCompanyName}`
            : normalizedLeadName || normalizedCompanyName
        );
      }

      setCustomSelections(prev => {
        const next = { ...prev };
        customObjectNames.forEach(objectName => {
          const existing = next[objectName] || { option: 'new', selected: null, newName: '' };
          if (!existing.newName) {
            existing.newName = normalizedLeadName || normalizedCompanyName;
          }
          next[objectName] = existing;
        });
        return next;
      });

      if (isAccountEnabled && companyName && !hasCheckedAccountRef.current) {
        hasCheckedAccountRef.current = true;
        checkExistingAccount();
      } else {
        // If no company field, just go to step 2
        setStep(2);
      }

    }
  }, [show, leadData, configuredObjects, isAccountEnabled, customObjectNames]);

  useEffect(() => {
    if (!show || !configuredObjects?.length) {
      return;
    }

    const objectConfigApi = new DataApi('objectconfig');
    objectConfigApi.post('/fetchconfigs', { objectNames: configuredObjects })
      .then((response) => {
        if (response?.data?.success && Array.isArray(response.data.data)) {
          const configMap = response.data.data.reduce((acc, config) => {
            acc[config.object_name] = config;
            return acc;
          }, {});
          setObjectConfigMap(configMap);
        }
      })
      .catch((error) => {
        console.warn('Failed to fetch object configs for conversion UI:', error);
      });
  }, [show, configuredObjects]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const leadName = getLeadName();
    const companyName = getCompanyName();
    const fallbackName = leadName || companyName || '';

    setCustomSelections(prev => {
      const next = { ...prev };
      customObjectNames.forEach(objectName => {
        const existing = next[objectName] || { option: 'new', selected: null, newName: '' };
        if (!existing.newName) {
          existing.newName = fallbackName;
        }
        next[objectName] = existing;
      });
      Object.keys(next).forEach(key => {
        if (!customObjectNames.includes(key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [show, customObjectNames, leadData]);

  useEffect(() => {
    if (!show) {
      return;
    }
    const leadCurrency = getLeadCurrency();
    if (!leadCurrency) {
      setError('Currency is required to convert a lead.');
    }
  }, [show, leadData]);

  const checkExistingAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      const companyName = getCompanyName();

      // Search for existing account with company name using /list/account route
      const dataApi = new DataApi('object/list/account');
      let response = await dataApi.fetchAll(`filter1=name:=:${encodeURIComponent(companyName)}`);

      if (response.data && response.data.data && response.data.data.length > 0) {
        // Found existing account - look for matching account
        const accounts = response.data.data;
        const matchingAccount = accounts.find(account =>
          account.name && account.name.value === companyName
        );

        if (matchingAccount) {
          const accountLabel = matchingAccount.name.value;
          setExistingAccount({
            id: matchingAccount.id.value,
            label: accountLabel
          });
          setSelectedAccount({
            id: matchingAccount.id.value,
            label: accountLabel
          });
        }
      }

      // Always move to step 2 to show the account selection
      setStep(2);
    } catch (err) {
      console.error('Error checking existing account:', err);
      setError('Failed to check for existing account');
      setStep(2); // Still show step 2 even if there's an error
    } finally {
      setLoading(false);
    }
  };


  const handleAccountSelect = (accountId, accountLabel) => {
    setSelectedAccount({ id: accountId, label: accountLabel });
  };

  const handleContactSelect = (contactId, contactLabel) => {
    setSelectedContact({ id: contactId, label: contactLabel });
  };

  const handleOpportunitySelect = (opportunityId, opportunityLabel) => {
    setSelectedOpportunity({ id: opportunityId, label: opportunityLabel });
  };

  const getObjectLabel = (objectName) => {
    const config = objectConfigMap[objectName];
    return config?.object_label || objectName;
  };

  const getNameField = (objectName) => {
    const config = objectConfigMap[objectName];
    const fields = config?.fields || [];
    const nameField = fields.find(field => field.field_name === 'name');
    if (nameField) {
      return nameField.field_name;
    }
    return fields[0]?.field_name || 'name';
  };

  const getLookupFieldConfig = (objectName) => {
    const config = objectConfigMap[objectName];
    const fields = config?.fields || [];
    const nameField = fields.find(field => field.field_name === 'name');
    const fallbackField = nameField || fields[0];
    const fieldName = fallbackField?.field_name || 'name';
    return {
      searchFields: fieldName,
      displayFields: fieldName
    };
  };

  const handleCustomOptionChange = (objectName, option) => {
    setCustomSelections(prev => ({
      ...prev,
      [objectName]: {
        ...prev[objectName],
        option
      }
    }));
  };

  const handleCustomSelect = (objectName, recordId, recordLabel) => {
    setCustomSelections(prev => ({
      ...prev,
      [objectName]: {
        ...prev[objectName],
        selected: { id: recordId, label: recordLabel }
      }
    }));
  };

  const handleCustomNameChange = (objectName, value) => {
    setCustomSelections(prev => ({
      ...prev,
      [objectName]: {
        ...prev[objectName],
        newName: value
      }
    }));
  };


  const handleConvert = async () => {
    try {
      if (!configuredObjects?.length) {
        setError('No conversion targets are available. Check subscription and lead conversion mapping.');
        return;
      }
      const leadCurrency = getLeadCurrency();
      if (!leadCurrency) {
        setError('Currency is required to convert a lead.');
        return;
      }
      setIsConverting(true);
      setError(null);

      // Step 1: Fetch field configurations for configured objects
      const objectNamesToFetch = configuredObjects;
      console.log('Fetching field configurations for:', objectNamesToFetch);
      const objectConfigApi = new DataApi('objectconfig');
      const configResponse = await objectConfigApi.post('/fetchconfigs', {
        objectNames: objectNamesToFetch
      });
      console.log('Object configurations:', configResponse);
      if (!configResponse.data || !configResponse.data.success) {
        throw new Error('Failed to fetch object field configurations');
      }

      const objectConfigs = configResponse.data.data;
      const configMap = objectConfigs.reduce((acc, config) => {
        acc[config.object_name] = config;
        return acc;
      }, {});
      setObjectConfigMap(configMap);
      console.log('Object configurations:', objectConfigs);

      // Step 2: Fetch lead field mappings
      console.log('Fetching lead field mappings...');


      const mappingApi = new DataApi('leadmapping');
      const mappingResponse = await mappingApi.get('/');

      if (!mappingResponse.data || !mappingResponse.data.success) {
        throw new Error('Failed to fetch lead field mappings');
      }

      const fieldMappings = mappingResponse.data.data;
      console.log('Field mappings:', fieldMappings);

      // Step 2.5: Fetch lead conversion configuration (redirect preference, default opportunity stage, and child mappings)
      let redirectPreference = 'opportunity'; // Default
      let defaultOpportunityStage = null;
      let mappingObjects = [];
      let savedChildObjectMappings = {};
      let dontMarkConverted = false;
      const leadConfig = await fetchLeadConversionConfig(false);
      redirectPreference = leadConfig.redirectPreference || 'opportunity';
      defaultOpportunityStage = leadConfig.defaultOpportunityStage || null;
      mappingObjects = Array.isArray(leadConfig.mappingObjects) ? leadConfig.mappingObjects : [];
      savedChildObjectMappings = leadConfig.childObjectMappings || {};
      dontMarkConverted = leadConfig.dontMarkConverted === true;
      console.log('Lead conversion config:', {
        redirectPreference,
        defaultOpportunityStage,
        mappingObjects,
        childObjectMappings: savedChildObjectMappings,
        dontMarkConverted
      });

      // Step 3: Process account
      let accountId = null;
      if (isAccountEnabled) {
        if (accountOption === 'existing') {
          accountId = selectedAccount?.id || existingAccount?.id;
          console.log('Updating existing account:', accountId);

          // Update existing account with mapped lead data
          const accountUpdateData = mapLeadFieldsToTarget('account', fieldMappings, leadData, objectConfigs);
          console.log('Account update data:', accountUpdateData);

          if (Object.keys(accountUpdateData).length > 0) {
            const accountApi = new DataApi(`object/account`);
            const accountUpdateResponse = await accountApi.update(accountUpdateData, accountId);

            if (!accountUpdateResponse.data || !accountUpdateResponse.data.success) {
              throw new Error('Failed to update existing account');
            }
            console.log('Account updated successfully');
          }
        } else {
          // Create new account
          console.log('Creating new account:', newAccountName);
          const accountData = mapLeadFieldsToTarget('account', fieldMappings, leadData, objectConfigs);
          accountData.name = newAccountName;

          const accountApi = new DataApi('object/account');
          const accountResponse = await accountApi.create(accountData);

          if (accountResponse.data && accountResponse.data.success) {
            accountId = accountResponse.data.data.id;
            console.log('Account created with ID:', accountId);
          } else {
            throw new Error('Failed to create account');
          }
        }
      }

      // Step 4: Process contact
      let contactId = null;
      if (isContactEnabled) {
        if (contactOption === 'existing') {
          contactId = selectedContact?.id;
          console.log('Updating existing contact:', contactId);

          // Update existing contact with mapped lead data
          const contactUpdateData = mapLeadFieldsToTarget('contact', fieldMappings, leadData, objectConfigs);
          console.log('Contact update data:', contactUpdateData);

          if (Object.keys(contactUpdateData).length > 0) {
            const contactApi = new DataApi(`object/contact`);
            const contactUpdateResponse = await contactApi.update(contactUpdateData, contactId);

            if (!contactUpdateResponse.data || !contactUpdateResponse.data.success) {
              throw new Error('Failed to update existing contact');
            }
            console.log('Contact updated successfully');
          }
        } else {
          // Create new contact
          console.log('Creating new contact:', newContactName);
          const contactData = mapLeadFieldsToTarget('contact', fieldMappings, leadData, objectConfigs);
          applyContactNameFromConvertInput(contactData, newContactName);
          if (accountId) {
            contactData.account_name = accountId; // Link to account
          }

          const contactApi = new DataApi('object/contact');
          const contactResponse = await contactApi.create(contactData);

          if (contactResponse.data && contactResponse.data.success) {
            contactId = contactResponse.data.data.id;
            console.log('Contact created with ID:', contactId);
          } else {
            throw new Error('Failed to create contact');
          }
        }
      }

      // Step 5: Process opportunity
      let opportunityId = null;
      if (isOpportunityEnabled) {
        if (opportunityOption === 'existing') {
          opportunityId = selectedOpportunity?.id;
          console.log('Updating existing opportunity:', opportunityId);

          // Update existing opportunity with mapped lead data
          const opportunityUpdateData = mapLeadFieldsToTarget('opportunity', fieldMappings, leadData, objectConfigs);
          console.log('Opportunity update data:', opportunityUpdateData);

          if (Object.keys(opportunityUpdateData).length > 0) {
            const opportunityApi = new DataApi(`object/opportunity`);
            const opportunityUpdateResponse = await opportunityApi.update(opportunityUpdateData, opportunityId);

            if (!opportunityUpdateResponse.data || !opportunityUpdateResponse.data.success) {
              throw new Error('Failed to update existing opportunity');
            }
            console.log('Opportunity updated successfully');
          }
        } else {
          // Create new opportunity
          console.log('Creating new opportunity:', newOpportunityName);
          const opportunityData = mapLeadFieldsToTarget('opportunity', fieldMappings, leadData, objectConfigs);
          opportunityData.name = newOpportunityName;
          if (accountId) {
            opportunityData.account_name = accountId; // Link to account
          }
          // Note: Contact linking might need to be handled differently based on opportunity schema

          // Apply default opportunity stage if configured and stage is not already set
          if (defaultOpportunityStage && !opportunityData.stage) {
            opportunityData.stage = defaultOpportunityStage;
            console.log(`Setting default opportunity stage: ${defaultOpportunityStage}`);
          }

          const opportunityApi = new DataApi('object/opportunity');
          const opportunityResponse = await opportunityApi.create(opportunityData);

          if (opportunityResponse.data && opportunityResponse.data.success) {
            opportunityId = opportunityResponse.data.data.id;
            console.log('Opportunity created with ID:', opportunityId);
          } else {
            throw new Error('Failed to create opportunity');
          }
        }
      }

      // Step 5.5: Process custom configured objects
      const customObjectIds = {};
      for (const objectName of customObjectNames) {
        const selection = customSelections[objectName] || { option: 'new', selected: null, newName: '' };
        if (selection.option === 'existing') {
          const existingId = selection.selected?.id;
          if (!existingId) {
            continue;
          }
          const updateData = mapLeadFieldsToTarget(objectName, fieldMappings, leadData, objectConfigs);
          if (Object.keys(updateData).length > 0) {
            const api = new DataApi(`object/${objectName}`);
            const updateResponse = await api.update(updateData, existingId);
            if (!updateResponse.data || !updateResponse.data.success) {
              throw new Error(`Failed to update existing ${objectName}`);
            }
          }
          customObjectIds[objectName] = existingId;
        } else {
          const createData = mapLeadFieldsToTarget(objectName, fieldMappings, leadData, objectConfigs);
          const nameField = getNameField(objectName);
          if (selection.newName?.trim()) {
            createData[nameField] = selection.newName.trim();
          }
          const api = new DataApi(`object/${objectName}`);
          const createResponse = await api.create(createData);
          if (createResponse.data && createResponse.data.success) {
            customObjectIds[objectName] = createResponse.data.data.id;
          } else {
            throw new Error(`Failed to create ${objectName}`);
          }
        }
      }

      // Step 6: Trigger async migration of related records based on configured targets
      // This is now handled by the backend asynchronously for better performance
      // Activities are automatically synced when their parent records (tasks, notes, email_logs) are updated
      const leadId = leadData.recordId || leadData.id || leadData.header?.id;
      if (allowMigration && leadId && opportunityId) {
        console.log('Triggering async migration from lead to opportunity...');
        console.log(`Lead ID: ${leadId}, Opportunity ID: ${opportunityId}`);

        try {
          const targetRecordIds = {
            account: accountId,
            contact: contactId,
            opportunity: opportunityId
          };
          Object.entries(customObjectIds).forEach(([objectName, recordId]) => {
            targetRecordIds[objectName] = recordId;
          });
          const effectiveChildMappings = buildDefaultChildMappings(savedChildObjectMappings);
          const filteredMappings = Object.entries(effectiveChildMappings).reduce((acc, [childKey, targetKey]) => {
            if (targetKey && targetKey !== 'skip' && targetRecordIds[targetKey]) {
              acc[childKey] = targetKey;
            }
            return acc;
          }, {});

          // Call backend endpoint to start async migration
          // The backend will process tasks, attachments, and quotes in batches
          const migrationApi = new DataApi('object/lead/migrate-to-opportunity');
          await migrationApi.post('', {
            leadId,
            opportunityId,
            targetRecordIds,
            childObjectMappings: filteredMappings
          });

          console.log('✅ Migration request sent to backend. Processing in background...');
        } catch (migrationError) {
          console.error('❌ Error triggering migration:', migrationError);
          // Don't fail the entire conversion if migration trigger fails
          // The backend will handle the actual migration asynchronously
        }
      } else if (!allowMigration) {
        console.warn('Skipping migration: migration is disabled by configuration');
      } else {
        console.warn('Cannot trigger migration: missing leadId or opportunityId', { leadId, opportunityId });
      }

      // Step 7: Update lead status. When admin enables "Don't mark Lead as Converted",
      // we keep the lead open (no is_converted, no closed status, no converted_date) but
      // still record the linkages to the new records so users have traceability.
      console.log('Updating lead with conversion linkages...', { dontMarkConverted });
      const leadApi = new DataApi(`object/lead`);
      const leadUpdatePayload = dontMarkConverted
        ? {
          lead_status: 'Closed - Converted',
          converted_account: accountId,
          converted_contact: contactId,
          converted_opportunity: opportunityId
        }
        : {
          lead_status: 'Closed - Converted',
          converted_account: accountId,
          converted_contact: contactId,
          converted_opportunity: opportunityId,
          converted_date: new Date().toISOString(),
          is_converted: true
        };
      await leadApi.update(leadUpdatePayload, leadData.recordId);


      // Success
      onConvert({
        success: true,
        message: 'Lead conversion completed successfully!',
        account: { id: accountId, name: accountOption === 'existing' ? (selectedAccount?.label || existingAccount?.label) : newAccountName },
        contact: { id: contactId, name: contactOption === 'existing' ? selectedContact?.label : newContactName },
        opportunity: { id: opportunityId, name: opportunityOption === 'existing' ? selectedOpportunity?.label : newOpportunityName },
        customObjects: customObjectIds,
        redirectPreference: redirectPreference
      });

      onHide();
    } catch (err) {
      console.error('Error converting lead:', err);
      setError(`Failed to convert lead: ${err.message}`);
    } finally {
      setIsConverting(false);
    }
  };

  // Helper function to map lead fields to target object fields
  const mapLeadFieldsToTarget = (targetObjectName, fieldMappings, leadData, objectConfigs) => {
    const mappedData = {};

    // Get the target object configuration
    const targetObjectConfig = objectConfigs.find(obj => obj.object_name === targetObjectName);
    if (!targetObjectConfig) {
      console.warn(`Object configuration not found for: ${targetObjectName}`);
      return mappedData;
    }

    // Get mappings for the target object
    const objectMappings = fieldMappings.filter(mapping =>
      mapping.target_object_name === targetObjectName
    );

    console.log(`Mappings for ${targetObjectName}:`, objectMappings);

    const leadFields = extractLeadFieldsFlat(leadData);

    // The lead id may not be stored under a direct `id` key on leadData (it lives on
    // recordId / header.id). Make it available so admins can map the Lead `id` field
    // to a lookup column on the target object (e.g. "Original Lead" lookup).
    const resolvedLeadId =
      leadFields.id ||
      leadData?.recordId ||
      leadData?.id ||
      leadData?.header?.id ||
      null;
    if (resolvedLeadId) {
      leadFields.id = resolvedLeadId;
    }

    console.log('Lead fields extracted:', leadFields);

    // Map lead fields to target object fields
    objectMappings.forEach(mapping => {
      const leadFieldValue = leadFields[mapping.lead_field_name];
      if (leadFieldValue !== undefined && leadFieldValue !== null && leadFieldValue !== '') {
        // Find the target field configuration to get the correct field name
        const targetField = targetObjectConfig.fields.find(field => field.id === mapping.target_field_id);
        if (targetField) {
          mappedData[targetField.field_name] = leadFieldValue;
          console.log(`Mapped ${mapping.lead_field_name} (${leadFieldValue}) -> ${targetField.field_name}`);
        } else {
          console.warn(`Target field not found for ID: ${mapping.target_field_id}`);
        }
      }
    });

    const targetKey = String(targetObjectName || '').toLowerCase();
    if (targetKey === 'contact') {
      applyStandardContactNameFromLead(mappedData, leadFields);
    }

    console.log(`Final mapped data for ${targetObjectName}:`, mappedData);
    return mappedData;
  };

  const handleClose = () => {
    setStep(1);
    setSelectedAccount(null);
    setExistingAccount(null);
    setError(null);
    setAccountOption('existing');
    setNewAccountName('');
    setContactOption('new');
    setSelectedContact(null);
    setExistingContact(null);
    setNewContactName('');
    setOpportunityOption('new');
    setSelectedOpportunity(null);
    setNewOpportunityName('');
    setCustomSelections({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-exchange-alt me-2"></i>
          Convert Lead
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {configuredObjects === null && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2 text-muted">Loading conversion options…</p>
          </div>
        )}

        {configuredObjects !== null && configuredObjects.length === 0 && (
          <Alert variant="warning" className="mb-0">
            No lead conversion targets match your subscription and admin mapping. Ask an admin to enable objects in the
            company plan and select them on the Lead object field mapping.
          </Alert>
        )}

        {configuredObjects !== null && loading && step === 1 && (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Checking for existing records...</p>
          </div>
        )}

        {configuredObjects !== null && configuredObjects.length > 0 && (step === 2 || (!loading && step === 1)) && (
          <div>

            {isAccountEnabled && (
              <div className="mb-4">
                {existingAccount ? (
                  <Alert variant="success" className="mb-3">
                    <strong>Existing Account Found:</strong><br />
                    {existingAccount.label}
                  </Alert>
                ) : (
                  <Alert variant="info" className="mb-3">
                    <strong>Account Options:</strong><br />
                    You can use an existing account or create a new one for "{getCompanyName()}".
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Account Selection</strong>
                    <span className="text-danger"> *</span>
                  </Form.Label>

                  <div className="mb-3">
                    <Form.Check
                      type="radio"
                      id="existing-account"
                      name="accountOption"
                      label="Use existing account"
                      checked={accountOption === 'existing'}
                      onChange={() => setAccountOption('existing')}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      id="new-account"
                      name="accountOption"
                      label="Create new account"
                      checked={accountOption === 'new'}
                      onChange={() => setAccountOption('new')}
                    />
                  </div>

                  {accountOption === 'existing' && (
                    <div>
                      <LookupLayout
                        objectName="account"
                        searchFields="name"
                        displayFields="name"
                        value={selectedAccount?.id}
                        defaultLabel={selectedAccount?.label || existingAccount?.label}
                        onChange={handleAccountSelect}
                        placeholder="Search for account..."
                        isRequired={true}
                      />
                      {existingAccount && (
                        <Form.Text className="text-muted">
                          Pre-selected: {existingAccount.label}
                        </Form.Text>
                      )}
                    </div>
                  )}

                  {accountOption === 'new' && (
                    <div>
                      <Form.Label className="mb-1">Account name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter new account name..."
                        value={newAccountName}
                        onChange={(e) => setNewAccountName(e.target.value)}
                        required
                        autoComplete="off"
                      />
                      <Form.Text className="text-muted">
                        This will create a new account with the name above.
                      </Form.Text>
                    </div>
                  )}
                </Form.Group>
              </div>
            )}


            {isContactEnabled && (
              <div className="mb-4">
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Contact Selection</strong>
                    <span className="text-danger"> *</span>
                  </Form.Label>

                  <div className="mb-3">
                    <Form.Check
                      type="radio"
                      id="existing-contact"
                      name="contactOption"
                      label="Use existing contact"
                      checked={contactOption === 'existing'}
                      onChange={() => setContactOption('existing')}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      id="new-contact"
                      name="contactOption"
                      label="Create new contact"
                      checked={contactOption === 'new'}
                      onChange={() => setContactOption('new')}
                    />
                  </div>

                  {contactOption === 'existing' && (
                    <div>
                      <LookupLayout
                        objectName="contact"
                        searchFields="first_name,last_name,email"
                        displayFields="first_name,last_name"
                        value={selectedContact?.id}
                        defaultLabel={selectedContact?.label}
                        onChange={handleContactSelect}
                        placeholder="Search for contact..."
                        isRequired={true}
                      />
                    </div>
                  )}

                  {contactOption === 'new' && (
                    <div>
                      <Form.Label className="mb-1">Contact name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter contact name..."
                        value={newContactName}
                        onChange={(e) => setNewContactName(e.target.value)}
                        required
                        autoComplete="off"
                      />
                      <Form.Text className="text-muted">
                        First word is saved as first name; remaining words as last name (you can edit before converting).
                      </Form.Text>
                    </div>
                  )}
                </Form.Group>
              </div>
            )}

            {isOpportunityEnabled && (
              <div className="mb-4">
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Opportunity Selection</strong>
                    <span className="text-danger"> *</span>
                  </Form.Label>

                  <div className="mb-3">
                    <Form.Check
                      type="radio"
                      id="existing-opportunity"
                      name="opportunityOption"
                      label="Use existing opportunity"
                      checked={opportunityOption === 'existing'}
                      onChange={() => setOpportunityOption('existing')}
                      className="mb-2"
                    />
                    <Form.Check
                      type="radio"
                      id="new-opportunity"
                      name="opportunityOption"
                      label="Create new opportunity"
                      checked={opportunityOption === 'new'}
                      onChange={() => setOpportunityOption('new')}
                    />
                  </div>

                  {opportunityOption === 'existing' && (
                    <div>
                      <LookupLayout
                        objectName="opportunity"
                        searchFields="name"
                        displayFields="name"
                        value={selectedOpportunity?.id}
                        defaultLabel={selectedOpportunity?.label}
                        onChange={handleOpportunitySelect}
                        placeholder="Search for opportunity..."
                        isRequired={true}
                      />
                    </div>
                  )}

                  {opportunityOption === 'new' && (
                    <div>
                      <Form.Label className="mb-1">Opportunity name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter opportunity name..."
                        value={newOpportunityName}
                        onChange={(e) => setNewOpportunityName(e.target.value)}
                        required
                        autoComplete="off"
                      />
                      <Form.Text className="text-muted">
                        This will create a new opportunity with the name above.
                      </Form.Text>
                    </div>
                  )}
                </Form.Group>
              </div>
            )}

            {customObjectNames.map((objectName) => {
              const selection = customSelections[objectName] || { option: 'new', selected: null, newName: '' };
              const { searchFields, displayFields } = getLookupFieldConfig(objectName);
              return (
                <div className="mb-4" key={objectName}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>{getObjectLabel(objectName)} Selection</strong>
                      <span className="text-danger"> *</span>
                    </Form.Label>

                    <div className="mb-3">
                      <Form.Check
                        type="radio"
                        id={`existing-${objectName}`}
                        name={`${objectName}Option`}
                        label={`Use existing ${getObjectLabel(objectName)}`}
                        checked={selection.option === 'existing'}
                        onChange={() => handleCustomOptionChange(objectName, 'existing')}
                        className="mb-2"
                      />
                      <Form.Check
                        type="radio"
                        id={`new-${objectName}`}
                        name={`${objectName}Option`}
                        label={`Create new ${getObjectLabel(objectName)}`}
                        checked={selection.option === 'new'}
                        onChange={() => handleCustomOptionChange(objectName, 'new')}
                      />
                    </div>

                    {selection.option === 'existing' && (
                      <div>
                        <LookupLayout
                          objectName={objectName}
                          searchFields={searchFields}
                          displayFields={displayFields}
                          value={selection.selected?.id}
                          defaultLabel={selection.selected?.label}
                          onChange={(id, label) => handleCustomSelect(objectName, id, label)}
                          placeholder={`Search for ${getObjectLabel(objectName)}...`}
                          isRequired={true}
                        />
                      </div>
                    )}

                    {selection.option === 'new' && (
                      <div>
                        <Form.Control
                          type="text"
                          placeholder={`Enter ${getObjectLabel(objectName)} name...`}
                          value={selection.newName || ''}
                          onChange={(e) => handleCustomNameChange(objectName, e.target.value)}
                          required
                        />
                      </div>
                    )}
                  </Form.Group>
                </div>
              );
            })}

          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>

        {configuredObjects !== null && configuredObjects.length > 0 && (step === 2 || (!loading && step === 1)) && (
          <Button
            variant="primary"
            onClick={handleConvert}
            disabled={
              isConverting ||
              !configuredObjects?.length ||
              !getLeadCurrency() ||
              (isAccountEnabled && accountOption === 'existing' && !selectedAccount && !existingAccount) ||
              (isAccountEnabled && accountOption === 'new' && !newAccountName.trim()) ||
              (isContactEnabled && contactOption === 'existing' && !selectedContact) ||
              (isContactEnabled && contactOption === 'new' && !newContactName.trim()) ||
              (isOpportunityEnabled && opportunityOption === 'existing' && !selectedOpportunity) ||
              (isOpportunityEnabled && opportunityOption === 'new' && !newOpportunityName.trim()) ||
              customObjectNames.some(objectName => {
                const selection = customSelections[objectName] || { option: 'new', selected: null, newName: '' };
                if (selection.option === 'existing') {
                  return !selection.selected?.id;
                }
                return !selection.newName?.trim();
              })
            }
          >
            {isConverting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Converting...
              </>
            ) : (
              <>
                <i className="fas fa-exchange-alt me-1"></i>
                Convert Lead
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default LeadConvertModal;
